<script setup lang="ts">
import { ref, computed } from 'vue'
import AltchaField from '@/Components/Forms/AltchaField.vue'
import { Button } from '@/Components/ui/button'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-vue-next'

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'switch-to', view: 'login' | 'signup'): void
}>()

const form = ref({
  altcha: '',
  email: '',
})

const altchaField = ref<InstanceType<typeof AltchaField> | null>(null)
const isSubmitting = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const successMessage = ref('')

const hasErrors = computed(() => Object.keys(errors.value).length > 0 || !!generalError.value)

async function submit() {
  isSubmitting.value = true
  errors.value = {}
  generalError.value = ''
  successMessage.value = ''

  try {
    await window.axios.post('/forgot-password', form.value)
    successMessage.value = 'We have emailed your password reset link! Check your inbox.'
    form.value.email = ''
  } catch (error: any) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors
    } else if (error.response?.data?.message) {
      generalError.value = error.response.data.message
    } else {
      generalError.value = 'An error occurred. Please try again.'
    }
  } finally {
    isSubmitting.value = false
    altchaField.value?.reset()
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">Forgot Password</h2>
      <p class="text-gray-600">Enter your email and we'll send you a reset link</p>
    </div>

    <form @submit.prevent="submit" class="space-y-5">
      <!-- Error Alert -->
      <Alert v-if="hasErrors" variant="destructive">
        <AlertCircle class="h-4 w-4" />
        <AlertDescription>
          {{ generalError || 'Please correct the errors below.' }}
        </AlertDescription>
      </Alert>

      <!-- Success Alert -->
      <Alert v-if="successMessage" class="border-green-500 text-green-700 [&>svg]:text-green-700">
        <CheckCircle2 class="h-4 w-4" />
        <AlertDescription>{{ successMessage }}</AlertDescription>
      </Alert>

      <!-- Email Field -->
      <div>
        <label for="forgot-email" class="block text-sm font-semibold text-gray-700 mb-2">
          Email Address <span class="text-red-500">*</span>
        </label>
        <input
          id="forgot-email"
          type="email"
          placeholder="john@example.com"
          v-model="form.email"
          required
          class="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-gray-900"
          :class="errors.email ? 'border-red-500' : 'border-gray-200'"
        />
        <p v-if="errors.email" class="text-sm text-red-500 mt-1">{{ errors.email[0] }}</p>
      </div>

      <div>
        <AltchaField ref="altchaField" v-model="form.altcha" />
        <p v-if="errors.altcha" class="text-sm text-red-500 mt-1">{{ errors.altcha[0] }}</p>
      </div>

      <Button
        type="submit"
        class="w-full bg-purple-600 hover:bg-purple-700 h-auto px-5 py-3.5 text-base font-semibold rounded-xl"
        :disabled="isSubmitting || !form.altcha"
      >
        {{ isSubmitting ? 'Sending...' : 'Send Reset Link' }}
      </Button>
    </form>

    <!-- Footer -->
    <div class="text-center">
      <button
        type="button"
        @click="$emit('switch-to', 'login')"
        class="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft class="w-4 h-4 mr-2" />
        Back to login
      </button>
    </div>
  </div>
</template>
